import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { OrderDetails } from './CheckoutView';

interface OrderSuccessViewProps {
  total: number;
  details: OrderDetails;
  onClose: () => void;
}

const paymentLabels: Record<string, string> = {
  card: 'Credit / Debit Card',
  mobile: 'Mobile Money',
  cash: 'Cash on Delivery',
  wallet: 'SipDash Wallet',
};

export default function OrderSuccessView({ total, details, onClose }: OrderSuccessViewProps) {
  const [orderId] = useState(() => `SDG-${Math.floor(10000 + Math.random() * 90000)}`);
  const [progress, setProgress] = useState(0);

  const formatPrice = (p: number) => `GYD $${p.toLocaleString()}`;

  useEffect(() => {
    const timer = setTimeout(() => setProgress(100), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Success animation area */}
      <div className="flex flex-col items-center justify-center px-6 pt-10 pb-6 text-center">
        <div className="relative mb-6">
          <div className="w-24 h-24 rounded-full bg-green-50 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <div className="w-10 h-10 flex items-center justify-center text-green-600">
                <i className="ri-checkbox-circle-fill text-4xl" />
              </div>
            </div>
          </div>
          <div className="absolute -top-1 -right-1 w-8 h-8 flex items-center justify-center rounded-full bg-[#FF6B35] text-white animate-bounce">
            <i className="ri-beer-line text-base" />
          </div>
        </div>

        <h2 className="text-2xl font-black text-gray-900 mb-1" style={{ fontFamily: "'Bebas Neue', cursive", letterSpacing: '0.04em' }}>
          Order Placed!
        </h2>
        <p className="text-gray-500 text-sm max-w-[260px]">
          Your beverages are being prepared. Sit tight — your rider is on the way!
        </p>

        <div className="mt-4 bg-[#FFF5F0] rounded-xl px-5 py-3 inline-flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center text-[#FF6B35]">
            <i className="ri-hashtag text-sm" />
          </div>
          <span className="text-sm font-black text-[#FF6B35] tracking-wide">{orderId}</span>
        </div>
      </div>

      {/* Delivery tracker */}
      <div className="px-6 mb-6">
        <div className="bg-white border border-gray-100 rounded-2xl p-5">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">Live Tracking</p>
          <div className="space-y-4">
            {[
              { label: 'Order Confirmed', icon: 'ri-file-list-3-line', done: true, active: false },
              { label: 'Preparing your order', icon: 'ri-store-2-line', done: false, active: true },
              { label: 'Out for delivery', icon: 'ri-motorbike-line', done: false, active: false },
              { label: 'Delivered!', icon: 'ri-checkbox-circle-line', done: false, active: false },
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-green-500 text-white' : step.active ? 'bg-[#FF6B35] text-white' : 'bg-gray-100 text-gray-400'}`}>
                  {step.done ? (
                    <i className="ri-check-line text-sm" />
                  ) : step.active ? (
                    <i className={`${step.icon} text-sm animate-pulse`} />
                  ) : (
                    <i className={`${step.icon} text-sm`} />
                  )}
                </div>
                <div className="flex-1">
                  <p className={`text-sm ${step.done || step.active ? 'font-semibold text-gray-900' : 'text-gray-400'}`}>{step.label}</p>
                  {step.active && <p className="text-xs text-[#FF6B35] mt-0.5">In progress...</p>}
                </div>
                {i < 3 && (
                  <div className="absolute ml-4 mt-8 w-px h-4 bg-gray-100" />
                )}
              </div>
            ))}
          </div>

          {/* ETA bar */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-500">Estimated arrival</span>
              <span className="text-sm font-bold text-green-600">20–35 min</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FF6B35] to-[#e85d2a] rounded-full transition-all duration-[2000ms] ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Order summary */}
      <div className="px-6 mb-6">
        <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Order Summary</p>
          <div className="flex items-start gap-2">
            <div className="w-4 h-4 flex items-center justify-center text-gray-400 mt-0.5 flex-shrink-0">
              <i className="ri-map-pin-line text-sm" />
            </div>
            <p className="text-sm text-gray-700">{details.address}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center text-gray-400 flex-shrink-0">
              <i className="ri-bank-card-line text-sm" />
            </div>
            <p className="text-sm text-gray-700">{paymentLabels[details.paymentMethod]}</p>
          </div>
          <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
            <div className="w-4 h-4 flex items-center justify-center text-[#FF6B35] flex-shrink-0">
              <i className="ri-price-tag-3-line text-sm" />
            </div>
            <p className="text-sm font-bold text-gray-900">Total: <span className="text-[#FF6B35]">{formatPrice(total)}</span></p>
          </div>

          {/* Change info — shown when Cash on Delivery with a valid change */}
          {details.paymentMethod === 'cash' && details.changeAmount !== undefined && details.changeAmount >= 0 && (
            <div className="mt-1 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3.5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-amber-100">
                    <i className="ri-coins-line text-amber-600 text-sm" />
                  </div>
                  <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">Seu Troco / Your Change</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs text-amber-700">
                    <i className="ri-arrow-right-s-line" />
                    <span>You pay: <strong>{formatPrice(details.cashAmount ?? 0)}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-amber-700">
                    <i className="ri-arrow-right-s-line" />
                    <span>Order total: <strong>{formatPrice(total)}</strong></span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-amber-600 font-medium mb-0.5">Rider brings back</p>
                  <p className="text-2xl font-black text-amber-700">{formatPrice(details.changeAmount)}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CTAs */}
      <div className="px-6 pb-6 space-y-3">
        <Link
          to="/tracking"
          onClick={onClose}
          className="w-full bg-[#FF6B35] hover:bg-[#e85d2a] text-white font-bold py-3.5 rounded-full flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-map-pin-time-line" />
          </div>
          Track My Order
        </Link>
        <button
          onClick={onClose}
          className="w-full border border-gray-200 text-gray-700 font-medium py-3 rounded-full hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm"
        >
          Continue Shopping
        </button>
      </div>
    </div>
  );
}
